// frontend/src/utils/htmlGenerator.js

// Elimina: import dayjs from 'dayjs'; // Ya no es necesario

/**
 * Genera el contenido HTML para una solicitud de servicio.
 * @param {object} data - Objeto con los datos de la solicitud.
 * @param {string} [data.customerName] - Nombre del cliente.
 * @param {string} [data.customerAddress] - Dirección del cliente.
 * @param {string} [data.customerEmail] - Email del cliente.
 * @param {string} [data.customerPhone] - Teléfono del cliente.
 * @param {string} [data.serviceName] - Nombre del servicio.
 * @param {string} [data.projectLocation] - Ubicación del proyecto.
 * @param {string} [data.additionalDetails] - Detalles adicionales.
 * @param {string} [data.customerNotes] - Notas del cliente.
 * @returns {string} - El string HTML completo del documento.
 */
export function generateServiceRequestHtml(data = {}) {
    // Puedes hacer el logo configurable o cargarlo desde tus assets
    const logoUrl = "https://servicios.biodiversa.com:1443/ado/Assets/logo-biodiversa-solo.svg";
    const currentYear = new Date().getFullYear();
    // Usando dayjs para formato local (requiere 'dayjs/plugin/localizedFormat')
    // import localizedFormat from 'dayjs/plugin/localizedFormat';
    // dayjs.extend(localizedFormat);
    // const currentDate = dayjs().format('DD/MM/YYYY HH:mm');
    // O usando Intl nativo:
    const currentDate = new Intl.DateTimeFormat('es-CL', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
    }).format(new Date());


    const logoHtml = logoUrl
        ? `<img src="${logoUrl}" alt="Logo Biodiversa S.A." class="logo" style="max-height: 60px; width: auto;">` // Ajusta estilos
        : `<span style="display:inline-block; width: 60px; height: 60px; background: #eee; text-align: center; line-height: 60px; font-size: 10px;">Logo</span>`; // Placeholder

    // Escapar HTML potencialmente inyectado en los datos (¡MUY IMPORTANTE!)
    const escapeHtml = (unsafe) => {
        if (!unsafe) return "-";
        return unsafe
             .replace(/&/g, "&amp;")
             .replace(/</g, "&lt;")
             .replace(/>/g, "&gt;")
             .replace(/"/g, "&quot;")
             .replace(/'/g, "&#039;");
     };

    // Plantilla HTML usando Template Literals de JavaScript
    // Incluye los estilos directamente o enlaza a un CSS externo si prefieres
    return `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Solicitud de Servicio - Biodiversa S.A.</title>
    <style>
        /* --- ESTILOS CSS --- */
        /* Importar fuente (opcional, puede ralentizar la carga inicial del PDF) */
        /* @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700&display=swap'); */

        body {
            /* font-family: 'Open Sans', sans-serif; */ /* Usa fuentes seguras para PDF o asegúrate que se embeben */
            font-family: Helvetica, Arial, sans-serif;
            margin: 0;
            padding: 0;
            color: #333;
            background-color: #fff; /* Fondo blanco para impresión */
            line-height: 1.5;
            font-size: 10pt; /* Tamaño base para impresión */
        }
        .print-container {
            width: 95%; /* Ajusta según necesidad */
            max-width: 800px;
            margin: 15px auto; /* Margen para visualización previa */
            padding: 15px;
            border: 1px solid #ccc; /* Borde visible */
            box-shadow: 0 0 8px rgba(0,0,0,0.1);
            border-radius: 5px;
        }
        header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #00447c; /* Color corporativo ejemplo */
            padding-bottom: 10px;
            margin-bottom: 20px;
        }
        .logo-container {
             display: flex;
             align-items: center;
        }
        .company-name {
            font-size: 1.5em; /* 15pt */
            color: #00447c;
            margin: 0 0 0 15px; /* Espacio después del logo */
            font-weight: 600;
        }
        .company-details {
            text-align: right;
            font-size: 0.9em; /* 9pt */
            color: #555;
        }
         .company-details p {
             margin: 2px 0;
         }
        .document-title {
            text-align: center;
            font-size: 1.4em; /* 14pt */
            font-weight: bold;
            margin-bottom: 25px;
            color: #333;
        }
        .section {
            margin-bottom: 20px;
        }
        .section-title {
            font-size: 1.2em; /* 12pt */
            font-weight: bold;
            color: #00447c;
            border-bottom: 1px solid #eee;
            padding-bottom: 5px;
            margin-bottom: 10px;
        }
        .client-details, .service-info {
            font-size: 1em; /* 10pt */
        }
        .client-details .info-item {
             margin-bottom: 5px;
        }
         .client-details p, .service-info p {
             margin: 4px 0;
         }
        .label {
            font-weight: bold;
            min-width: 90px; /* Alineación simple */
            display: inline-block;
        }
        .service-details {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #eee;
        }
        pre { /* Para mantener formato en detalles/notas */
            white-space: pre-wrap; /* Ajuste de línea */
            word-wrap: break-word; /* Romper palabras largas */
            background-color: #f9f9f9;
            border: 1px solid #eee;
            padding: 8px;
            border-radius: 4px;
            font-size: 0.95em; /* 9.5pt */
            font-family: Consolas, 'Courier New', monospace;
        }
        footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 0.8em; /* 8pt */
            color: #777;
        }

        /* --- ESTILOS PARA IMPRESIÓN --- */
        @media print {
            body {
                margin: 0;
                padding: 0;
                background-color: #fff;
                font-size: 10pt; /* Asegurar tamaño base */
                 /* Evitar que Chrome añada header/footer propios */
                /* page-break-before: always; */ /* Control de saltos de página si es necesario */
            }
            /* Ocultar todo excepto el contenedor a imprimir */
            body > *:not(.print-container-wrapper) {
                 /* display: none !important; */ /* CUIDADO: esto puede ocultar el contenedor si no está directo en body */
            }
             /* Asegurar que el contenedor ocupe el ancho y no tenga bordes/sombras */
            .print-container-wrapper { /* Un div que envuelva al .print-container */
                 display: block !important;
            }
            .print-container {
                 width: 100% !important;
                 max-width: none !important;
                 margin: 0 !important;
                 padding: 0 !important;
                 border: none !important;
                 box-shadow: none !important;
                 border-radius: 0 !important;
                 /* Controlar saltos de página internos */
                 page-break-inside: avoid;
            }
            header, .section {
                 page-break-inside: avoid; /* Evitar cortar secciones */
            }
            .section-title {
                page-break-after: avoid; /* Evitar salto justo después del título */
            }
            pre {
                 page-break-inside: avoid;
                 background-color: #fff !important; /* Fondo blanco para impresión */
                 border: 1px solid #ccc !important; /* Borde más visible si se desea */
            }
            footer {
                 page-break-before: auto; /* Permitir que el footer se mueva si es necesario */
            }
            /* Ocultar botones o elementos no deseados en la impresión */
            .no-print {
                display: none !important;
            }
        }
    </style>
</head>
<body>
    <div class="print-container-wrapper">
         <div class="print-container">
              <header>
                  <div class="logo-container">
                      ${logoHtml}
                      <h1 class="company-name">Biodiversa S.A.</h1>
                  </div>
                  <div class="company-details">
                      <p>Avda. Arturo Prat 199 Piso 12, torre B<br>Concepción, Biobío</p>
                      <p>Tel: (41) 234 7734</p>
                      <p>Email: contacto@biodiversa.com</p>
                  </div>
              </header>
              <div class="document-title">
                  Solicitud de Servicio: ${escapeHtml(data.serviceName) || "Servicio General"}
              </div>
              <section class="section">
                  <h3 class="section-title">Datos del Solicitante</h3>
                  <div class="client-details">
                       <p><span class="label">Cliente:</span> ${escapeHtml(data.customerName)}</p>
                       <p><span class="label">Dirección:</span> ${escapeHtml(data.customerAddress)}</p>
                       <p><span class="label">Email:</span> ${escapeHtml(data.customerEmail)}</p>
                       <p><span class="label">Teléfono:</span> ${escapeHtml(data.customerPhone)}</p>
                       <p><span class="label">Fecha Solicitud:</span> ${currentDate}</p>
                  </div>
              </section>
              <section class="section">
                   <h3 class="section-title">Servicio Solicitado</h3>
                   <div class="service-info">
                       <p><span class="label">Tipo de servicio:</span> ${escapeHtml(data.serviceName) || "No especificado"}</p>
                       <p><span class="label">Ubicación:</span> ${escapeHtml(data.projectLocation)}</p>
                       <div class="service-details">
                           <strong>Detalles Adicionales:</strong>
                           <pre>${escapeHtml(data.additionalDetails)}</pre>
                       </div>
                   </div>
               </section>
               <section class="section">
                   <h3 class="section-title">Notas Adicionales del Cliente</h3>
                   <pre>${escapeHtml(data.customerNotes) || "Sin observaciones."}</pre>
               </section>
               <footer>
                   <p>Generado automáticamente por Sistema de Asistente IA</p>
                   <p>© ${currentYear} Biodiversa S.A. - Todos los derechos reservados</p>
               </footer>
         </div>
    </div>
</body>
</html>
    `.trim(); // trim() quita espacios/saltos de línea iniciales/finales del string
}